import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ExecutionStats } from '../../../models/test-case.model';
import { TestCaseService } from '../../../services/test-case.service';

type CountKey = keyof Omit<ExecutionStats, 'passRate'>;

interface LegendItem {
  keys: readonly CountKey[];
  modifier: string;
  labelKey: string;
  color: string;
}

@Component({
  selector: 'app-test-case-execution-stats',
  imports: [TranslatePipe],
  templateUrl: './test-case-execution-stats.component.html',
  styleUrl: './test-case-execution-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseExecutionStatsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);

  readonly stats = signal<ExecutionStats | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);

  readonly legend: readonly LegendItem[] = [
    {
      keys: ['passed'],
      modifier: 'passed',
      labelKey: 'testCases.form.executionStats.passed',
      color: 'var(--tf-success-fg)',
    },
    {
      keys: ['failed'],
      modifier: 'failed',
      labelKey: 'testCases.form.executionStats.failed',
      color: 'var(--tf-error-fg)',
    },
    {
      keys: ['blocked'],
      modifier: 'blocked',
      labelKey: 'testCases.form.executionStats.blocked',
      color: 'var(--tf-warning-fg)',
    },
    {
      keys: ['notRun', 'running', 'skipped'],
      modifier: 'not-run',
      labelKey: 'testCases.form.executionStats.notRun',
      color: 'var(--tf-chart-not-run)',
    },
  ];

  readonly total = computed(() => this.legend.reduce((sum, item) => sum + this.legendValue(item), 0));

  readonly hasData = computed(() => this.total() > 0);

  /**
   * conic-gradient für den Donut. Die Stopps werden aus den ungerundeten
   * Anteilen in Legendenreihenfolge kumuliert, damit kein Rundungsspalt
   * entsteht; der letzte Status füllt bis 100 %.
   */
  readonly donutBackground = computed(() => {
    const total = this.total();
    if (total === 0) {
      return 'var(--tf-border-soft)';
    }
    let start = 0;
    const segments = this.legend.map((item, index) => {
      const from = index === 0 ? '0' : `${start}%`;
      const end = index === this.legend.length - 1 ? 100 : start + (this.legendValue(item) / total) * 100;
      start = end;
      return `${item.color} ${from} ${end}%`;
    });
    return `conic-gradient(${segments.join(', ')})`;
  });

  readonly ariaParams = computed(() => {
    const [passed, failed, blocked, notRun] = this.legend.map((item) => this.legendValue(item));
    return { passRate: this.stats()?.passRate ?? 0, passed, failed, blocked, notRun };
  });

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      const testCaseId = this.testCaseId();
      if (!testCaseId) {
        this.stats.set(null);
        this.loading.set(false);
        this.error.set(false);
        return;
      }
      this.loadStats(projectId, testCaseId);
    });
  }

  private loadStats(projectId: string, testCaseId: string): void {
    this.loading.set(true);
    this.error.set(false);
    this.testCaseService
      .getExecutionStats(projectId, testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.stats.set(stats);
          this.loading.set(false);
        },
        error: () => {
          this.stats.set(null);
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  legendValue(item: LegendItem): number {
    const s = this.stats();
    return s ? item.keys.reduce((sum, key) => sum + (s[key] ?? 0), 0) : 0;
  }
}
